/**
 * @file useRepDashboard.js
 * @version 1.1.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-08
 *
 * One fetch hook for the rep widgets, so four components do not each grow their
 * own loading/error/abort handling.
 *
 * The endpoints refuse a non-rep with 403 NOT_A_REP, and a REP with no linked
 * rep with 403 OUT_OF_SCOPE. Both are expected states rather than faults, so
 * they come back as a `message` the widget can render plainly instead of a
 * console error and an empty box.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import { formatQuantity, formatCount, formatTonnes as formatTonnesRule } from '../utils/numberFormatters';

/**
 * Tonnes always render with a capital T (project rule), and by the quantity
 * rule (Cam, 2026-10-08): "120.00 T", "12.30 T", "12.375 T". The rule is
 * utils/numberFormatters; this keeps the widgets' '—' for a missing figure.
 */
export const formatTonnes = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return formatTonnesRule(n);
};

/**
 * A volume with whatever unit it is actually measured in.
 *
 * The Tonnes Sold card no longer always reports tonnes: a selection of tote or
 * colostrum codes is counted, not weighed, and formatTonnes' hardcoded " T"
 * would have labelled a count of buckets as a tonnage. The tonnes case still
 * renders a capital T, per the project rule.
 *
 * Tonnes follow the quantity rule ("4,523.00"); a count of totes or buckets
 * is a count — only the places it has ("12"; Cam, 2026-10-08).
 */
export const volumeFigure = (value, unit = 'T') => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (unit === 'T' ? formatQuantity : formatCount)(n);
};

export const formatVolume = (value, unit = 'T') => {
  const figure = volumeFigure(value, unit);
  return figure === '—' ? figure : `${figure} ${unit}`;
};

/** Compact money for a widget tile: £1.2m, £340k, £950. */
export const formatCompactMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `£${Math.round(n / 1_000)}k`;
  return `£${Math.round(n)}`;
};

export default function useRepDashboard(path, { params = {}, skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [message, setMessage] = useState(null);

  // Serialised so an inline params object does not re-fire the effect forever.
  const key = JSON.stringify(params);
  const abortRef = useRef(null);

  /*
   * `force` sends refresh=1, which makes the server recompute this card for
   * this rep and replace what it had stored. That is what the refresh button on
   * each card does — the ordinary load is served from the daily 07:00 warm.
   */
  const load = useCallback(async (force = false) => {
    if (skip) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setMessage(null);
    try {
      const qs = new URLSearchParams({
        ...JSON.parse(key),
        ...(force ? { refresh: '1' } : {})
      }).toString();
      const res = await fetch(`${API_URL}/api/rep-dashboard/${path}${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
        signal: controller.signal
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        // A refusal is a state to show, not an error to swallow.
        setMessage(body.detail || body.error || 'Could not load this');
        setData(null);
        return;
      }
      setData(body);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessage('Could not load this');
      setData(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [path, key, skip]);

  useEffect(() => {
    // Not `load` directly: it now takes an argument, and useEffect would hand
    // it the effect's own cleanup slot on some paths.
    load(false);
    return () => abortRef.current?.abort();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, loading, message, reload: () => load(false), refresh };
}
